/**
 * TourHelpButton — the "page tutorials" button.
 *
 * Mounted once in the Navbar: it automatically appears on any page that has
 * tours the current user is eligible for (menuToursForCurrentPage is already
 * audience-filtered by the provider) and always opens a dropdown listing ALL
 * of that page's tutorials — every tab/surface, not just the visible one —
 * grouped under their registry menuSection headings. Cross-route entries
 * (menuAlso, e.g. the Newsletter Editor listed on My Marketing) are included
 * and launch through startTourFromMenu, which navigates first.
 * A green check marks tutorials completed at their current version.
 * Renders nothing on pages without tours or while a tour is running.
 *
 * Daily nudge: the first time each day a user lands on a page with tours, a
 * small dismissible pill slides in under the button ("Need help? Take a quick
 * tour") — no overlay, never blocks clicks elsewhere, auto-hides after a few
 * seconds. Clicking the pill opens the tutorials dropdown (the X just
 * dismisses). Once per user per day via localStorage (`originTourNudge:<uid>`);
 * any tour starting that day (auto or manual) counts as the nudge and
 * suppresses it.
 *
 * Also exports <TourReplayButton tourId="..." /> for optional inline
 * placement in page headers.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FiCheckCircle, FiHelpCircle, FiPlayCircle } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureTour } from './FeatureTourProvider';

const NUDGE_SHOW_DELAY_MS = 2500; // after the auto-start window (800ms) so a starting tour wins
const NUDGE_AUTO_HIDE_MS = 8000;

const nudgeStorageKey = (uid) => `originTourNudge:${uid || 'anon'}`;

/** Local (not UTC) YYYY-MM-DD — "once a day" should follow the user's clock. */
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function readNudgeDate(uid) {
  try {
    return window.localStorage.getItem(nudgeStorageKey(uid));
  } catch (_) {
    return todayLocal(); // storage blocked → behave as already shown today
  }
}

function writeNudgeDate(uid) {
  try {
    window.localStorage.setItem(nudgeStorageKey(uid), todayLocal());
  } catch (_) {
    // storage blocked — worst case the nudge shows again after a reload
  }
}

const nudgeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Soft expanding ring that fades out — "flashy but gentle". Runs 3 times.
const nudgePulse = keyframes`
  0%, 100% { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22), 0 0 0 0 rgba(66, 153, 225, 0.5); }
  50%      { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22), 0 0 0 9px rgba(66, 153, 225, 0); }
`;

export default function TourHelpButton() {
  const { menuToursForCurrentPage, startTourFromMenu, activeTour, seenTours } = useFeatureTour();
  const { currentUser } = useAuth();

  // All hooks above the early return (react-hooks/rules-of-hooks).
  const [showNudge, setShowNudge] = useState(false);
  const hideTimerRef = useRef(null);
  // Controlled so the nudge pill can open the tutorials dropdown itself.
  const menu = useDisclosure();
  const nudgeBg = useColorModeValue('blue.500', 'blue.400');
  const nudgeHoverBg = useColorModeValue('blue.600', 'blue.500');

  const uid = currentUser?.uid;
  const hasTours = menuToursForCurrentPage.length > 0;

  // Group entries by menuSection, preserving registry order (first appearance
  // fixes each section's position). Untitled entries form their own group.
  const menuGroups = useMemo(() => {
    const groups = [];
    const byTitle = new Map();
    menuToursForCurrentPage.forEach((entry) => {
      const key = entry.section || '';
      if (!byTitle.has(key)) {
        const group = { title: key, items: [] };
        byTitle.set(key, group);
        groups.push(group);
      }
      byTitle.get(key).items.push(entry);
    });
    return groups;
  }, [menuToursForCurrentPage]);
  const hasSections = menuGroups.some((group) => group.title);

  // A tour running today = the user already knows tours exist. Count it as
  // the day's nudge and hide any visible pill.
  useEffect(() => {
    if (!activeTour) return;
    setShowNudge(false);
    if (uid) writeNudgeDate(uid);
  }, [activeTour, uid]);

  // First tours-capable page of the day: slide the nudge in after a short
  // grace (an auto-starting tour claims the screen first and suppresses it).
  useEffect(() => {
    if (!hasTours || activeTour || !uid) return undefined;
    if (readNudgeDate(uid) === todayLocal()) return undefined;

    const showTimer = setTimeout(() => {
      writeNudgeDate(uid);
      setShowNudge(true);
      hideTimerRef.current = setTimeout(() => setShowNudge(false), NUDGE_AUTO_HIDE_MS);
    }, NUDGE_SHOW_DELAY_MS);
    return () => clearTimeout(showTimer);
  }, [hasTours, activeTour, uid]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    []
  );

  if (!hasTours || activeTour) return null;

  return (
    <Box position="relative" display="inline-flex">
      <Menu
        placement="bottom-end"
        isOpen={menu.isOpen}
        onOpen={() => {
          setShowNudge(false);
          menu.onOpen();
        }}
        onClose={menu.onClose}
      >
        <Tooltip label="Page tutorials" hasArrow>
          <MenuButton
            as={IconButton}
            aria-label="Page tutorials"
            icon={<FiHelpCircle />}
            size="lg"
            variant="ghost"
            background="none"
          />
        </Tooltip>
        <MenuList zIndex={1500} maxH="70vh" overflowY="auto">
          {menuGroups.map((group, groupIndex) => (
            <React.Fragment key={group.title || '__general'}>
              {groupIndex > 0 && <MenuDivider />}
              <MenuGroup title={group.title || (hasSections ? 'General' : 'Page tutorials')}>
                {group.items.map((entry) => {
                  const { tour } = entry;
                  const seen = seenTours ? seenTours[tour.id] : null;
                  const completed =
                    !!seen &&
                    seen.status === 'completed' &&
                    (seen.version || 0) >= (tour.version || 1);
                  return (
                    <MenuItem
                      key={tour.id}
                      icon={
                        <Icon
                          as={completed ? FiCheckCircle : FiPlayCircle}
                          color={completed ? 'green.400' : 'blue.400'}
                          boxSize={4}
                        />
                      }
                      onClick={() => startTourFromMenu(entry)}
                    >
                      {tour.title}
                    </MenuItem>
                  );
                })}
              </MenuGroup>
            </React.Fragment>
          ))}
        </MenuList>
      </Menu>

      {showNudge && (
        <HStack
          role="status"
          position="absolute"
          top="calc(100% + 10px)"
          right={0}
          zIndex={1300}
          bg={nudgeBg}
          _hover={{ bg: nudgeHoverBg }}
          color="white"
          px={3}
          py={2}
          spacing={2}
          borderRadius="lg"
          whiteSpace="nowrap"
          maxW="calc(100vw - 24px)"
          cursor="pointer"
          onClick={() => {
            setShowNudge(false);
            menu.onOpen();
          }}
          animation={`${nudgeSlideIn} 0.25s ease-out, ${nudgePulse} 2s ease-in-out 0.3s 3`}
          boxShadow="0 6px 18px rgba(0, 0, 0, 0.22)"
        >
          {/* Caret pointing up at the help button */}
          <Box
            position="absolute"
            top="-4px"
            right="18px"
            w="8px"
            h="8px"
            bg="inherit"
            transform="rotate(45deg)"
          />
          <Icon as={FiHelpCircle} boxSize={4} flexShrink={0} />
          <Text fontSize="sm" fontWeight="medium">
            Need help? Take a quick tour
          </Text>
          <CloseButton
            size="sm"
            color="whiteAlpha.800"
            _hover={{ color: 'white', bg: 'whiteAlpha.300' }}
            aria-label="Dismiss reminder"
            onClick={(e) => {
              e.stopPropagation();
              setShowNudge(false);
            }}
          />
        </HStack>
      )}
    </Box>
  );
}

/** Inline variant for page headers: replays one specific tour. */
export function TourReplayButton({ tourId, children = 'Show tutorial', ...buttonProps }) {
  const { startTour } = useFeatureTour();
  return (
    <Button
      size="sm"
      variant="ghost"
      leftIcon={<FiHelpCircle />}
      onClick={() => startTour(tourId, { source: 'manual' })}
      {...buttonProps}
    >
      {children}
    </Button>
  );
}
